(() => {
  const methods = {
    initCarousel() {
      if (this._btsVcAbort) this._btsVcAbort.abort();
      this._btsVcAbort = new AbortController();
      const { signal } = this._btsVcAbort;

      this.viewport = this.querySelector('[data-bts-vc-viewport]');
      this.track = this.querySelector('[data-bts-vc-track]');
      this.prevButton = this.querySelector('[data-bts-vc-prev]');
      this.nextButton = this.querySelector('[data-bts-vc-next]');
      this.progress = this.querySelector('[data-bts-vc-progress]');
      this.progressBar = this.querySelector('[data-bts-vc-progress-bar]');
      this.cards = Array.from(this.querySelectorAll('[data-bts-vc-card]'));
      this.playRequestMap = this.playRequestMap || new WeakMap();
      this.hoveredCard = null;
      this.scrollEndTimer = null;

      if (!this.viewport || !this.cards.length) return;

      this.cards.forEach((card) => this.bindCard(card, signal));
      this.prevButton?.addEventListener('click', () => this.scrollByCard(-1), { signal });
      this.nextButton?.addEventListener('click', () => this.scrollByCard(1), { signal });
      this.progress?.addEventListener('click', (event) => this.onProgressClick(event), { signal });
      this.viewport.addEventListener('scroll', () => this.onScroll(), { passive: true, signal });
      window.addEventListener('resize', () => this.onResize(), { signal });
      this.addEventListener('click', (event) => this.onPlayClick(event), { signal });

      this.scrollToLeft(this.getDefaultScroll(), true);
      this.update();
      requestAnimationFrame(() => {
        this.scrollToLeft(this.getDefaultScroll(), true);
        this.update();
      });
    },

    teardownCarousel() {
      if (this._btsVcAbort) this._btsVcAbort.abort();
      this._btsVcAbort = null;
      if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
    },

    bindCard(card, signal) {
      const media = card.querySelector('[data-bts-vc-media]');
      const video = card.querySelector('video');
      if (!media) return;

      const canHover = window.matchMedia('(hover: hover)').matches;

      if (video && video.dataset.bound !== 'true') {
        video.dataset.bound = 'true';
        if (canHover) video.controls = false;
        video.addEventListener('play', () => media.classList.add('is-playing'));
        video.addEventListener('pause', () => media.classList.remove('is-playing'));
        video.addEventListener('ended', () => media.classList.remove('is-playing'));
      }

      card.addEventListener(
        'mouseenter',
        () => {
          this.hoveredCard = card;
          this.updateCenterCard();
          if (video && canHover) {
            video.controls = true;
            media.classList.add('is-controls-visible');
          }
          this.playCard(card);
        },
        { signal }
      );

      card.addEventListener(
        'mouseleave',
        () => {
          if (this.hoveredCard === card) this.hoveredCard = null;
          if (video && canHover) {
            video.controls = false;
            media.classList.remove('is-controls-visible');
          }
          this.stopCard(card);
          this.updateCenterCard();
        },
        { signal }
      );

      media.addEventListener('touchstart', () => this.playCard(card), { passive: true, signal });
    },

    onPlayClick(event) {
      const playButton = event.target.closest('[data-bts-vc-play]');
      if (!playButton || !this.contains(playButton)) return;
      const card = playButton.closest('[data-bts-vc-card]');
      this.playCard(card, true);
    },

    pauseOthers(activeCard) {
      this.cards.forEach((card) => {
        if (card === activeCard) return;
        this.stopCard(card);
      });
    },

    async playCard(card, withSound = false) {
      const media = card?.querySelector('[data-bts-vc-media]');
      const video = card?.querySelector('video');
      if (!media || !video) return;

      if (!video.paused && !video.ended) {
        media.classList.add('is-playing');
        return;
      }

      const nextId = (this.playRequestMap.get(card) || 0) + 1;
      this.playRequestMap.set(card, nextId);
      this.pauseOthers(card);
      media.classList.remove('is-playing');

      try {
        video.muted = !withSound;
        await video.play();
        if (this.playRequestMap.get(card) !== nextId) return;
        if (!video.paused && !video.ended) media.classList.add('is-playing');
      } catch (error) {
        if (this.playRequestMap.get(card) === nextId) media.classList.remove('is-playing');
      }
    },

    stopCard(card) {
      const media = card?.querySelector('[data-bts-vc-media]');
      const video = card?.querySelector('video');
      if (!media || !video) return;
      this.playRequestMap.set(card, (this.playRequestMap.get(card) || 0) + 1);
      video.pause();
      media.classList.remove('is-playing');
    },

    pauseAll() {
      this.cards.forEach((card) => this.stopCard(card));
    },

    getCardWidth() {
      return this.cards[0]?.offsetWidth || 219;
    },

    getGap() {
      if (!this.track || this.cards.length < 2) {
        const styles = this.track ? window.getComputedStyle(this.track) : null;
        return styles ? parseFloat(styles.columnGap || styles.gap) || 16 : 16;
      }
      return Math.max(0, this.cards[1].offsetLeft - this.cards[0].offsetLeft - this.cards[0].offsetWidth);
    },

    getStride() {
      return this.getCardWidth() + this.getGap();
    },

    getRawMaxScroll() {
      if (!this.viewport) return 0;
      return Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
    },

    /* One stop per card, each centering that card in the viewport, so the
       leftover space is always split evenly between the left and right peek.
       Clamping to [0, rawMax] makes the ends show the first/last card in full. */
    getScrollStops() {
      const rawMax = this.getRawMaxScroll();
      if (rawMax <= 0 || !this.viewport) return [0];

      const stride = this.getStride();
      const width = this.getCardWidth();
      const viewportWidth = this.viewport.clientWidth;
      if (stride <= 0 || width <= 0 || viewportWidth <= 0) return [0];

      const stops = this.cards.map((_, index) => {
        const centered = index * stride + width / 2 - viewportWidth / 2;
        return Math.round(Math.max(0, Math.min(rawMax, centered)));
      });

      return [...new Set(stops)].sort((a, b) => a - b);
    },

    getDefaultScroll() {
      const stops = this.getScrollStops();
      return stops.find((stop) => stop > 0) ?? 0;
    },

    nearestStopIndex(scrollLeft = this.viewport?.scrollLeft || 0) {
      const stops = this.getScrollStops();
      let best = 0;
      let bestDist = Infinity;
      stops.forEach((stop, index) => {
        const dist = Math.abs(stop - scrollLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = index;
        }
      });
      return best;
    },

    scrollToLeft(left, instant = false) {
      if (!this.viewport) return;
      const rawMax = this.getRawMaxScroll();
      const target = Math.max(0, Math.min(left, rawMax));
      this.viewport.scrollTo({
        left: target,
        behavior: instant ? 'auto' : 'smooth',
      });
    },

    scrollByCard(direction) {
      if (!this.viewport) return;
      const stops = this.getScrollStops();
      if (stops.length < 2) return;

      const currentIndex = this.nearestStopIndex();
      const nextIndex = Math.max(0, Math.min(stops.length - 1, currentIndex + direction));
      if (nextIndex === currentIndex) return;

      this.pauseAll();
      this.scrollToLeft(stops[nextIndex], false);
    },

    snapToNearestStop(instant = false) {
      if (!this.viewport) return;
      const stops = this.getScrollStops();
      if (!stops.length) return;
      const target = stops[this.nearestStopIndex()];
      if (Math.abs(this.viewport.scrollLeft - target) <= 1) return;
      this.scrollToLeft(target, instant);
    },

    onProgressClick(event) {
      if (!this.viewport || !this.progress) return;
      const rect = this.progress.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
      const rawMax = this.getRawMaxScroll();
      this.pauseAll();
      this.scrollToLeft(ratio * rawMax, false);
      window.setTimeout(() => this.snapToNearestStop(false), 280);
    },

    updateProgress() {
      if (!this.progressBar || !this.viewport) return;
      const rawMax = this.getRawMaxScroll();
      const ratio = rawMax > 0 ? this.viewport.scrollLeft / rawMax : 0;
      const thumb = Math.max(28, Math.min(48, (this.viewport.clientWidth / this.viewport.scrollWidth) * 100 || 36));
      const travel = Math.max(0, 100 - thumb);
      this.progressBar.style.width = `${thumb}%`;
      this.progressBar.style.marginLeft = `${Math.max(0, Math.min(1, ratio)) * travel}%`;
    },

    updateNav() {
      if (!this.viewport) return;
      const rawMax = this.getRawMaxScroll();
      const canScroll = rawMax > 1;
      const atStart = this.viewport.scrollLeft <= 1;
      const atEnd = this.viewport.scrollLeft >= rawMax - 1;

      if (this.prevButton) {
        this.prevButton.disabled = !canScroll || atStart;
        this.prevButton.classList.toggle('is-active', canScroll && !atStart);
      }
      if (this.nextButton) {
        this.nextButton.disabled = !canScroll || atEnd;
        this.nextButton.classList.toggle('is-active', canScroll && !atEnd);
      }
    },

    /* Middle of the 5 full cards: index from viewport center. */
    getCenterCardIndex() {
      if (!this.viewport || !this.cards.length) return -1;
      const stride = this.getStride();
      const width = this.getCardWidth();
      if (stride <= 0 || width <= 0) return -1;

      const centerX = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      const index = Math.round((centerX - width / 2) / stride);
      return Math.max(0, Math.min(this.cards.length - 1, index));
    },

    updateCenterCard() {
      if (!this.viewport || !this.cards.length) return;

      const viewportLeft = this.viewport.scrollLeft;
      const viewportRight = viewportLeft + this.viewport.clientWidth;
      const centerIndex = this.getCenterCardIndex();
      const centerCard = this.cards[centerIndex] || null;

      let leftEdgeCard = null;
      let rightEdgeCard = null;
      let leftMinDistance = Number.POSITIVE_INFINITY;
      let rightMinDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        card.classList.remove('is-center', 'is-edge-left', 'is-edge-right');

        const cardLeft = index * this.getStride();
        const cardRight = cardLeft + this.getCardWidth();
        const visible = Math.min(cardRight, viewportRight) - Math.max(cardLeft, viewportLeft);
        if (visible <= 0) return;

        if (cardLeft < viewportLeft + 1) {
          const leftDistance = Math.abs(cardLeft - viewportLeft);
          if (leftDistance < leftMinDistance) {
            leftMinDistance = leftDistance;
            leftEdgeCard = card;
          }
        }
        if (cardRight > viewportRight - 1) {
          const rightDistance = Math.abs(cardRight - viewportRight);
          if (rightDistance < rightMinDistance) {
            rightMinDistance = rightDistance;
            rightEdgeCard = card;
          }
        }
      });

      if (leftEdgeCard && leftEdgeCard !== centerCard) leftEdgeCard.classList.add('is-edge-left');
      if (rightEdgeCard && rightEdgeCard !== centerCard) rightEdgeCard.classList.add('is-edge-right');

      /* Hovering another card temporarily replaces the default center scale. */
      if (this.hoveredCard && this.hoveredCard !== centerCard) return;
      centerCard?.classList.add('is-center');
    },

    update() {
      this.updateNav();
      this.updateProgress();
      this.updateCenterCard();
    },

    onScroll() {
      this.update();
      if (this.scrollEndTimer) window.clearTimeout(this.scrollEndTimer);
      this.scrollEndTimer = window.setTimeout(() => this.snapToNearestStop(false), 140);
    },

    onResize() {
      this.snapToNearestStop(true);
      this.update();
    },
  };

  const Existing = customElements.get('bts-video-carousel');
  if (Existing) {
    Object.assign(Existing.prototype, methods);
    document.querySelectorAll('bts-video-carousel').forEach((el) => {
      if (typeof el.initCarousel === 'function') el.initCarousel();
    });
    return;
  }

  class BtsVideoCarousel extends HTMLElement {
    connectedCallback() {
      this.initCarousel();
    }

    disconnectedCallback() {
      this.teardownCarousel();
    }
  }

  Object.assign(BtsVideoCarousel.prototype, methods);
  customElements.define('bts-video-carousel', BtsVideoCarousel);
})();
